import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { apiTokenRequest } from '../authConfig';
import { msalInstance } from '../msalInstance';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function acquireAccessToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error('No authenticated account — sign in required');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(apiTokenRequest);
      throw err;
    }
    throw err;
  }
}

/**
 * The active environment's Manager origin, written by `EnvironmentProvider`.
 *
 * FD-421 — the last compiled-in console value to go. Until now the base was
 * `import.meta.env.VITE_MANAGER_API_BASE_URL`, baked into the bundle at build
 * time, which is precisely why one build could serve exactly one environment.
 * FD-420 moved every OTHER value to the runtime `/config.json` and honestly
 * marked this one as still outstanding; FD-ADR-040 §7 assigns it here, to "a
 * module-level store the provider writes".
 *
 * A module-level `let` rather than context, because `authFetch` is a plain async
 * function called from query functions — it has no React tree to read from. The
 * provider writes it DURING RENDER, before any descendant effect can fire a
 * query, so every request an environment makes is addressed to that environment.
 * See `EnvironmentProvider` for why that ordering is load-bearing rather than
 * incidental.
 */
let activeManagerBaseUrl: string | null = null;

/** Called by `EnvironmentProvider` on every render of the active environment. */
export function setActiveManagerBaseUrl(baseUrl: string): void {
  activeManagerBaseUrl = baseUrl;
}

/** Test-only reset. Production has exactly one writer. */
export function clearActiveManagerBaseUrl(): void {
  activeManagerBaseUrl = null;
}

/**
 * Resolve the origin every admin request is sent to.
 *
 * DEV returns `''` — relative URLs, so the Vite dev-server proxy makes the hop
 * to the Manager and the request never trips its CORS allowlist (which names
 * only the SWA origin). That behaviour is unchanged and deliberately preserved.
 *
 * In a built console an unset base THROWS rather than falling back to `''`. A
 * relative URL there would silently address the CONSOLE's own origin — a request
 * aimed at one environment, delivered somewhere else, with a 404 as the only
 * symptom. Sending an admin request to the wrong target without saying so is the
 * exact hazard FD-ADR-040's selector, badge and destructive-dialog echo exist to
 * prevent, so this fails by name instead. It is unreachable in practice: `App`
 * renders inside `EnvironmentProvider`.
 */
export function resolveManagerBaseUrl(): string {
  if (import.meta.env.DEV) return '';
  if (activeManagerBaseUrl === null) {
    throw new Error(
      'No active environment: an API request was made before EnvironmentProvider ' +
        'resolved one. This is a boot-order defect — every caller must render ' +
        'inside the provider.',
    );
  }
  return activeManagerBaseUrl;
}

/**
 * Fetch against an EXPLICITLY NAMED environment, bypassing the active one.
 *
 * FD-422. Every other request in the console goes to whichever environment is
 * active, which is exactly right for anything an operator asked for. The boot
 * probe is the one thing that is not: it asks EVERY environment in the fleet
 * "may I?" so the answer is the landing experience rather than something
 * discovered by clicking (FD-ADR-040 §8).
 *
 * The token is the same token — one MSAL instance, one cached access token,
 * every Manager in the fleet (see `msalInstance.ts`). Only the origin differs.
 *
 * Deliberately NOT a variant of `setActiveManagerBaseUrl`: the module store has
 * exactly one writer and a probe that moved it would repoint every in-flight
 * query at another environment for as long as the probe ran. That is the hazard
 * this whole arc exists to prevent, arriving from its own mitigation.
 */
export async function authFetchAt(
  baseUrl: string,
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const token = await acquireAccessToken();
  return fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      ...init?.headers,
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function authFetch(path: string, init?: RequestInit): Promise<Response> {
  return authFetchAt(resolveManagerBaseUrl(), path, init);
}

export interface JsonResponse<T> {
  status: number;
  body: T;
}

// The canonical ADR-007 envelope (FD-081/FD-082) carries no body-level
// statusCode, so callers that need to distinguish sibling success codes
// (200 all-processed vs 207 partial on add/remove-devices) read the HTTP
// status from here.
export async function getJsonWithStatus<T>(
  path: string,
  init?: RequestInit,
): Promise<JsonResponse<T>> {
  return readEnvelope<T>(await authFetch(path, init));
}

/**
 * The envelope read against an explicitly named environment (FD-422).
 * Same parsing, same `ApiError`, same `error.code` in `body` — so a refusal from
 * a probed environment is indistinguishable in shape from one on the active
 * environment, and `isForbiddenTenantError` has exactly one thing to recognise.
 */
export async function getJsonAt<T>(
  baseUrl: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  return (await readEnvelope<T>(await authFetchAt(baseUrl, path, init))).body;
}

async function readEnvelope<T>(response: Response): Promise<JsonResponse<T>> {
  const text = await response.text();
  const body = text ? safeParse(text) : undefined;

  if (!response.ok) {
    const envelopeMessage =
      body && typeof body === 'object' && 'error' in body
        ? extractErrorMessage((body as { error: unknown }).error)
        : response.statusText;
    throw new ApiError(response.status, envelopeMessage || `Request failed (${response.status})`, body);
  }

  return { status: response.status, body: body as T };
}

export async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  return (await getJsonWithStatus<T>(path, init)).body;
}

export async function postJsonWithStatus<TResponse, TBody = unknown>(
  path: string,
  body: TBody,
): Promise<JsonResponse<TResponse>> {
  return getJsonWithStatus<TResponse>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function postJson<TResponse, TBody = unknown>(
  path: string,
  body: TBody,
): Promise<TResponse> {
  return (await postJsonWithStatus<TResponse, TBody>(path, body)).body;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractErrorMessage(error: unknown): string | undefined {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    return typeof message === 'string' ? message : undefined;
  }
  return undefined;
}
